import '../../../styles/InteractiveComponents.css';

import React, { useEffect, useState } from 'react';
import {
  SorterCategory,
  SorterItem
} from '../../../models/LessonComponentContent';
import { cloneDeep, isEmpty, isNil } from 'lodash';

import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Constants from '../../../constants';
import Grid from '@mui/material/Unstable_Grid2';
import LessonResponseService from '../../../services/LessonResponseService';
import NewComponentService from '../../../services/NewComponentService';
import { SorterCategoryEditor } from './SorterCategoryEditor';
import { SorterComponent } from '../../../models/LessonComponent';
import { useLessonEditContext } from '../../../models/LessonEditContext';

interface SorterEditorProps {
  component: SorterComponent;
}

export function SorterEditor(props: SorterEditorProps) {
  const { handleSettingsChange } = useLessonEditContext();
  const sorterCategories = props.component.content.categories;
  const [categories, setCategories] =
    useState<SorterCategory[]>(sorterCategories);

  useEffect(() => {
    const copy = cloneDeep(props.component);
    copy.content.categories = cloneDeep(categories);
    const correctAnswerPattern =
      LessonResponseService.getSorterResponsePattern(categories);
    //set correctResponsePattern and call handleSettingsChange if there is any category items added
    if (correctAnswerPattern) {
      copy.content.correctResponsePattern = correctAnswerPattern;
      handleSettingsChange(copy);
    }
  }, [categories]);

  useEffect(() => {
    if (sorterCategories) {
      setCategories(sorterCategories);
    }
  }, [sorterCategories]);

  function handleAddCategory() {
    setCategories((value) => {
      const copy = cloneDeep(value);
      copy.push(NewComponentService.createSorterCategory());
      return copy;
    });
  }

  function handleDeleteCategory(id: string) {
    setCategories((categories) => {
      const copy = cloneDeep(categories);
      const index = copy.findIndex((c) => c.id === id);
      if (index < 0) {
        throw new Error('Category not found.');
      }
      copy.splice(index, 1);
      return copy;
    });
  }

  function handleSaveCategoryLabel(id: string, label: string) {
    setCategories((categories) => {
      const copy = cloneDeep(categories);
      const category = copy.find((c) => c.id === id);
      if (!category) {
        throw new Error('Category not found.');
      }
      category.label = label;

      return copy;
    });
  }

  function handleDeleteItem(categoryId: string, itemId: string) {
    setCategories((currentValue) => {
      const copy = cloneDeep(currentValue);
      const category = copy.find((c) => c.id === categoryId);
      const index = category?.items.findIndex((c) => c.id === itemId);
      if (index < 0) {
        throw new Error('Item not found.');
      }
      category.items.splice(index, 1);
      return copy;
    });
  }

  function handleSaveItem(categoryId: string, updatedItem: SorterItem) {
    if (isEmpty(updatedItem?.label) && isNil(updatedItem?.image)) {
      //* Not allowed to save an item until either a label or an image is set.
      return;
    }
    setCategories((currentValue) => {
      const copy = cloneDeep(currentValue);
      const category = copy.find((c) => c.id === categoryId);
      const index = category?.items.findIndex((c) => c.id === updatedItem.id);
      if (index < 0) {
        //* New item
        category.items.push(updatedItem);
      } else {
        category.items.splice(index, 1, updatedItem);
      }
      return copy;
    });
  }

  return (
    <Grid container direction="column">
      <Grid>
        <SorterCategoryEditor
          categories={categories}
          onDeleteCategory={handleDeleteCategory}
          onSaveCategoryLabel={handleSaveCategoryLabel}
          onDeleteItem={handleDeleteItem}
          onSaveItem={handleSaveItem}
        />
      </Grid>
      <Grid>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          disabled={categories?.length >= Constants.SorterMaxNumCategories}
          onClick={handleAddCategory}
        >
          Click to add a category
        </Button>
      </Grid>
    </Grid>
  );
}
